import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Desktop hover cart preview for the header cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} panel
 * @property {HTMLElement} items
 * @property {HTMLElement} empty
 * @property {HTMLElement} footer
 * @property {HTMLElement} subtotal
 * @property {HTMLAnchorElement} trigger
 *
 * @extends {Component<Refs>}
 */
class CartPreview extends Component {
  requiredRefs = ['panel', 'items', 'empty', 'footer', 'subtotal', 'trigger'];

  /** @type {number | undefined} */
  #hideTimer;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.#onEnter);
    this.addEventListener('mouseleave', this.#onLeave);
    this.refs.trigger.addEventListener('focus', this.#onEnter);
    this.addEventListener('focusout', this.#onFocusOut);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener('cart:update', this.#onLegacyCartUpdate);

    // Touch devices: keep click-to-cart only, no sticky preview.
    if (window.matchMedia('(hover: none)').matches) {
      this.setAttribute('data-hover-disabled', '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('mouseenter', this.#onEnter);
    this.removeEventListener('mouseleave', this.#onLeave);
    this.refs.trigger?.removeEventListener('focus', this.#onEnter);
    this.removeEventListener('focusout', this.#onFocusOut);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener('cart:update', this.#onLegacyCartUpdate);
    window.clearTimeout(this.#hideTimer);
  }

  #onEnter = () => {
    if (this.hasAttribute('data-hover-disabled')) return;
    window.clearTimeout(this.#hideTimer);
    this.#show();
  };

  #onLeave = () => {
    if (this.hasAttribute('data-hover-disabled')) return;
    window.clearTimeout(this.#hideTimer);
    this.#hideTimer = window.setTimeout(() => this.#hide(), 160);
  };

  #onFocusOut = () => {
    requestAnimationFrame(() => {
      if (!this.contains(document.activeElement)) this.#hide();
    });
  };

  #show() {
    this.refs.panel.hidden = false;
    this.setAttribute('data-open', '');
    this.refs.trigger.setAttribute('aria-expanded', 'true');
  }

  #hide() {
    this.refs.panel.hidden = true;
    this.removeAttribute('data-open');
    this.refs.trigger.setAttribute('aria-expanded', 'false');
  }

  #onCartUpdate = (event) => {
    event.promise?.then(() => this.#refreshFromApi()).catch(() => this.#refreshFromApi());
  };

  #onLegacyCartUpdate = () => {
    this.#refreshFromApi();
  };

  async #refreshFromApi() {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      this.#renderFromCart(cart);
    } catch (error) {
      console.warn('[cart-preview] Failed to refresh cart', error);
    }
  }

  /**
   * @param {Record<string, any>} cart
   */
  #renderFromCart(cart) {
    const items = Array.isArray(cart.items) ? cart.items : [];
    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    const empty = items.length === 0;
    const itemCount = Number(cart.item_count) || items.length;

    this.refs.empty.hidden = !empty;
    this.refs.items.hidden = empty;
    this.refs.footer.hidden = empty;

    const header = this.refs.panel.querySelector('.cart-preview__header');
    let countEl = header?.querySelector('.cart-preview__count');
    if (empty) {
      countEl?.remove();
    } else if (countEl) {
      countEl.textContent = String(itemCount);
    } else if (header) {
      countEl = document.createElement('p');
      countEl.className = 'cart-preview__count';
      countEl.textContent = String(itemCount);
      header.appendChild(countEl);
    }

    this.refs.items.innerHTML = items
      .slice(0, 5)
      .map((item) => {
        const image = item.image
          ? `<img class="cart-preview__image" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title || '')}" width="56" height="56" loading="lazy">`
          : `<div class="cart-preview__image cart-preview__image--placeholder" aria-hidden="true"></div>`;
        const title = escapeHtml(item.product_title || item.title || '');
        const variant =
          item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-preview__variant">${escapeHtml(item.variant_title)}</p>`
            : '';
        const qty = Number(item.quantity) || 1;
        const price = formatMoney(item.final_line_price ?? item.line_price ?? 0, moneyFormat);

        return `
          <a class="cart-preview__item" href="${escapeHtml(item.url || '/cart')}">
            ${image}
            <div class="cart-preview__item-copy">
              <p class="cart-preview__item-title">${title}</p>
              ${variant}
              <p class="cart-preview__item-meta">Qty ${qty} · ${price}</p>
            </div>
          </a>
        `;
      })
      .join('');

    if (items.length > 5) {
      this.refs.items.insertAdjacentHTML(
        'beforeend',
        `<p class="cart-preview__more">+${items.length - 5} more in cart</p>`
      );
    }

    this.refs.subtotal.textContent = formatMoney(cart.total_price ?? 0, moneyFormat);
  }
}

/**
 * @param {number} cents
 * @param {string} format
 */
function formatMoney(cents, format) {
  const value = (Number(cents) / 100).toFixed(2);
  const valueNoDecimals = String(Math.round(Number(cents) / 100));
  return format
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','))
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, valueNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, valueNoDecimals)
    .replace(/\{\{\s*amount\s*\}\}/g, value);
}

/** @param {string} value */
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

if (!customElements.get('cart-preview')) {
  customElements.define('cart-preview', CartPreview);
}
